'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { gridIdToPath } from '@/lib/utils';
import { rawYieldToUsd } from '@/features/lobby/data/grid-to-board';
import { HV_TAKEOVER_GRID_ID } from '@/lib/constants/addresses';
import { useTakeoverPrice } from './use-takeover-price';
import { useEthPrice } from './use-eth-price';
import type { MockBoard } from '@/features/lobby/data/mock-boards';

/**
 * A single land holder on a board, with resolved identity for the
 * avatar + level flair. Mirrors the grid endpoint's identity-enriched
 * `topHolders` entries (already sorted by seat count).
 */
export interface BoardHolder {
  address: string;
  seatCount: number;
  displayName?: string;
  avatarUrl?: string | null;
  level?: number;
}

interface BoardLand {
  holders: BoardHolder[];
  /** Sum of self-assessed prices of tax-paying (non-creator) seats, in
   *  the tax token's raw units. Convert to USD with the tax token. */
  landValueRaw: string;
  taxToken: string;
  taxTokenDecimals: number;
}

const EMPTY: BoardLand = {
  holders: [],
  landValueRaw: '0',
  taxToken: '',
  taxTokenDecimals: 18,
};

/**
 * "Land value" of a board = the sum of the self-assessed price of every
 * tax-paying seat (i.e. seats NOT held by the board creator). This is a
 * different quantity from the asset/pool TVL (`totalTvlUsd`) — that's
 * the value locked in the underlying yield asset, not what holders have
 * priced their land at.
 *
 * The grid endpoint's `topHolders` already carries each holder's
 * cumulative self-assessed `totalValue` (raw tax-token units) plus the
 * board creator, so we sum every non-creator holder's `totalValue`.
 */
async function fetchBoardLand(gridId: string): Promise<BoardLand> {
  const res = await fetch(`/api/grid/${gridIdToPath(gridId)}`);
  if (!res.ok) return EMPTY;
  const json = await res.json();
  const th = (json?.topHolders ?? json?.data?.topHolders ?? []) as Array<{
    address?: string;
    seatCount?: number;
    totalValue?: string;
    displayName?: string;
    avatarUrl?: string | null;
    level?: number;
  }>;
  const grid = json?.grid ?? json?.data?.grid ?? {};
  const creator = String(grid.creator ?? grid.creator_id ?? '').toLowerCase();

  let sum = 0n;
  const holders: BoardHolder[] = [];
  for (const h of th) {
    const address = String(h.address ?? '');
    holders.push({
      address,
      seatCount: Number(h.seatCount ?? 0),
      displayName: h.displayName,
      avatarUrl: h.avatarUrl,
      level: h.level,
    });
    // Exclude the creator's own (non-tax-paying) inventory.
    if (address.toLowerCase() !== creator) {
      try {
        sum += BigInt(h.totalValue ?? '0');
      } catch {
        /* ignore unparseable */
      }
    }
  }

  return {
    holders,
    landValueRaw: sum.toString(),
    taxToken: String(grid.taxToken ?? ''),
    taxTokenDecimals: Number(grid.taxTokenDecimals ?? 18),
  };
}

export interface BoardLandResult {
  landValueUsd: number;
  holders: BoardHolder[];
  isLoading: boolean;
}

/**
 * Fetches land value + holders for a set of boards in parallel (one
 * cached query per board, keyed by grid id so the hero and the
 * territories leaderboard share fetches). Returns a per-board lookup
 * plus the summed total.
 *
 * `enabled` gates the (heavy) per-board grid fetches; pass `false` until
 * the data is actually needed.
 *
 * NOTE: each board costs a full grid-endpoint fetch. This is fine for
 * the bounded active-board set, but a server-side `landValueUsd` field
 * on `/api/v1/grids` would remove the per-board round-trips entirely —
 * worth doing before the homepage takes production traffic.
 */
export function useBoardsLand(
  boards: MockBoard[],
  enabled = true,
): {
  byBoardId: Map<string, BoardLandResult>;
  totalUsd: number;
  isLoading: boolean;
} {
  const { priceUsd: takeoverUsd } = useTakeoverPrice();
  const { ethPrice } = useEthPrice();

  const gridIds = boards.map((b) =>
    b.id === 'boardroom' ? HV_TAKEOVER_GRID_ID : b.id,
  );

  const results = useQueries({
    queries: gridIds.map((id) => ({
      queryKey: ['board-land', id] as const,
      queryFn: () => fetchBoardLand(id),
      enabled: enabled && !!id,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    })),
  });

  return useMemo(() => {
    const byBoardId = new Map<string, BoardLandResult>();
    let totalUsd = 0;
    let isLoading = false;
    boards.forEach((board, i) => {
      const r = results[i];
      const land = r?.data;
      const usd = land
        ? rawYieldToUsd(land.landValueRaw, land.taxToken, land.taxTokenDecimals, {
            takeoverUsd,
            ethUsd: ethPrice,
          })
        : 0;
      if (r?.isLoading) isLoading = true;
      if (land) totalUsd += usd;
      byBoardId.set(board.id, {
        landValueUsd: usd,
        holders: land?.holders ?? [],
        isLoading: !!r?.isLoading,
      });
    });
    return { byBoardId, totalUsd, isLoading };
    // `results` identity changes each render; depend on the data tuple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, takeoverUsd, ethPrice, JSON.stringify(results.map((r) => [r?.status, !!r?.data]))]);
}
